// İSG Portal — Form alanı sarmalayıcısı (Radix Label + hata metni). Erişilebilir:
// label `htmlFor` ile input'a bağlanır, hata `role="alert"`.

import * as LabelPrimitive from '@radix-ui/react-label';
import type { ReactNode } from 'react';
import { cn } from './cn';

interface FieldProps {
  /** Input `id`'si — label ve hata metni bağlaması için zorunlu. */
  htmlFor: string;
  label: ReactNode;
  /**
   * Etiketin YANINDA (aynı satırda) ama `<label>` elemanının DIŞINDA render edilir. Bir
   * tetikleyici (ör. `Tooltip`'in `<button>`'ı) `label` içine gömülürse native
   * label-for-forwarding onu da `htmlFor` hedefine tıklamış sayar — ör. bir Select'i açar.
   * Odaklanabilir herhangi bir eleman etikete EK olarak gerekiyorsa buraya, `label`e DEĞİL.
   */
  labelExtra?: ReactNode;
  /** Çözülmüş hata metni (i18n ile) — verilirse gösterilir. */
  error?: string | null;
  /** Alan altında yardımcı açıklama. */
  hint?: ReactNode;
  required?: boolean;
  children: ReactNode;
  className?: string;
}

/**
 * Input'un `aria-describedby`'ı — `Field`'in kendi hata/ipucu paragraf kimlikleri (`Field` input'u
 * sarmalamaz, bağlamayı çağıran yapar). Hata varken ipucu çizilmez, bu yüzden yalnız hata bağlanır.
 */
export function fieldDescribedBy(
  htmlFor: string,
  error: string | null | undefined,
  hasHint: boolean,
): string | undefined {
  if (error) {
    return `${htmlFor}-error`;
  }
  return hasHint ? `${htmlFor}-hint` : undefined;
}

export function Field({
  htmlFor,
  label,
  labelExtra,
  error,
  hint,
  required,
  children,
  className,
}: FieldProps) {
  const errorId = `${htmlFor}-error`;
  const hintId = `${htmlFor}-hint`;
  return (
    <div className={cn('flex flex-col gap-1.5 carbon:gap-2', className)}>
      <div className="flex items-center gap-1.5">
        {/* carbon: (S33 Stage 2 "Input/Select/Textarea" satırı) — 12px, muted, geniş harf
            aralığı, üstten `mb-2` yerine `gap-2` (dış kabın kendi boşluğu). */}
        <LabelPrimitive.Root
          htmlFor={htmlFor}
          className="text-sm font-medium text-fg carbon:text-xs carbon:tracking-[.32px] carbon:text-muted"
        >
          {label}
          {required ? <span className="ml-0.5 text-danger">*</span> : null}
        </LabelPrimitive.Root>
        {labelExtra}
      </div>
      {children}
      {hint && !error ? (
        <p id={hintId} className="text-xs text-muted">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className="text-xs font-medium text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}
